import { isValidUsageEventField } from '@monetize/constants';
import {
  InvoiceLineRule,
  InvoiceTransformationAvailableField,
  InvoiceTransformationBillGroupOverride,
  InvoiceTransformationCondition,
  InvoiceTransformationConditionGroup,
  InvoiceTransformationConditionOperatorEnum,
  InvoiceTransformationOutputModeEnum,
  InvoiceTransformationRequest,
  InvoiceTransformationRequestUi,
  InvoiceTransformationResponse,
  InvoiceTransformationTargetEnum,
} from '@monetize/types/app';
import { formatInteger, pluralize } from '@monetize/utils/core';

export type BillGroupOverrideKey = keyof InvoiceTransformationBillGroupOverride;

type BillGroupOverrideRow = {
  key: BillGroupOverrideKey;
  value: string;
};

export const OPERATOR_LABELS: Record<
  InvoiceTransformationConditionOperatorEnum,
  string
> = {
  [InvoiceTransformationConditionOperatorEnum.EQ]: 'Equals',
  [InvoiceTransformationConditionOperatorEnum.NE]: 'Not Equals',
  [InvoiceTransformationConditionOperatorEnum.IN]: 'In',
  [InvoiceTransformationConditionOperatorEnum.NOT_IN]: 'Not In',
  [InvoiceTransformationConditionOperatorEnum.CONTAINS]: 'Contains',
};

export const OPERATOR_OPTIONS = (
  Object.keys(OPERATOR_LABELS) as InvoiceTransformationConditionOperatorEnum[]
).map((value) => ({ value, title: OPERATOR_LABELS[value] }));

export const MULTI_VALUE_OPERATORS: ReadonlySet<InvoiceTransformationConditionOperatorEnum> =
  new Set([
    InvoiceTransformationConditionOperatorEnum.IN,
    InvoiceTransformationConditionOperatorEnum.NOT_IN,
  ]);

export function isMultiValueOperator(
  operator: InvoiceTransformationConditionOperatorEnum,
): boolean {
  return MULTI_VALUE_OPERATORS.has(operator);
}

export const TARGET_OPTIONS: Array<{
  value: InvoiceTransformationTargetEnum;
  title: string;
}> = [
  {
    value: InvoiceTransformationTargetEnum.INVOICE_ITEM,
    title: 'Invoice Item',
  },
  { value: InvoiceTransformationTargetEnum.USAGE_EVENT, title: 'Usage Event' },
];

// Line transformations currently only support usage-event targets.
export function getTargetOptions(
  mode: InvoiceTransformationOutputModeEnum,
): typeof TARGET_OPTIONS {
  if (mode === InvoiceTransformationOutputModeEnum.LINE_TRANSFORMATION) {
    return TARGET_OPTIONS.filter(
      ({ value }) => value === InvoiceTransformationTargetEnum.USAGE_EVENT,
    );
  }
  return TARGET_OPTIONS;
}

export function getDefaultTarget(
  mode: InvoiceTransformationOutputModeEnum,
): InvoiceTransformationTargetEnum {
  return mode === InvoiceTransformationOutputModeEnum.LINE_TRANSFORMATION
    ? InvoiceTransformationTargetEnum.USAGE_EVENT
    : InvoiceTransformationTargetEnum.INVOICE_ITEM;
}

export const OUTPUT_MODE_OPTIONS: Array<{
  value: InvoiceTransformationOutputModeEnum;
  title: string;
  description: string;
}> = [
  {
    value: InvoiceTransformationOutputModeEnum.MULTIPLE_INVOICES,
    title: 'Split into multiple invoices',
    description:
      'Group matching items into separate invoice documents, with optional bill group overrides per invoice.',
  },
  {
    value: InvoiceTransformationOutputModeEnum.LINE_TRANSFORMATION,
    title: 'Split lines on a single invoice',
    description:
      'Keep one invoice but split matching items into multiple lines based on your conditions.',
  },
];

export function isValidUsageEventFieldPath(value: string): boolean {
  return isValidUsageEventField(value);
}

export const OVERRIDE_KEY_OPTIONS: Array<{
  value: BillGroupOverrideKey;
  title: string;
}> = [
  { value: 'purchaseOrderNumber', title: 'PO Number' },
  { value: 'vatNumber', title: 'VAT Number' },
  { value: 'registrationNumber', title: 'Registration Number' },
  { value: 'locale', title: 'Locale' },
  { value: 'billingContactId', title: 'Billing Contact' },
  { value: 'shippingContactId', title: 'Shipping Contact' },
];

const CONTACT_OVERRIDE_KEYS: ReadonlySet<BillGroupOverrideKey> = new Set([
  'billingContactId',
  'shippingContactId',
]);

export function isContactOverrideKey(key: BillGroupOverrideKey): boolean {
  return CONTACT_OVERRIDE_KEYS.has(key);
}

// Bill group overrides only make sense when splitting into separate invoices.
export function supportsBillGroupOverrides(
  mode: InvoiceTransformationOutputModeEnum,
): boolean {
  return mode === InvoiceTransformationOutputModeEnum.MULTIPLE_INVOICES;
}

export type FieldKind =
  | 'OFFERING_REF'
  | 'OFFERING_TYPE'
  | 'PRODUCT_REF'
  | 'PRODUCT_TYPE'
  | 'RATE_REF'
  | 'STRING';

const FIELD_KIND_BY_PATH: Record<string, FieldKind> = {
  offeringId: 'OFFERING_REF',
  'offering.id': 'OFFERING_REF',
  offeringType: 'OFFERING_TYPE',
  'offering.type': 'OFFERING_TYPE',
  productId: 'PRODUCT_REF',
  'product.id': 'PRODUCT_REF',
  productType: 'PRODUCT_TYPE',
  'product.type': 'PRODUCT_TYPE',
  rateId: 'RATE_REF',
  'rate.id': 'RATE_REF',
};

export function getFieldKind(fieldPath: string | undefined): FieldKind {
  if (!fieldPath) {
    return 'STRING';
  }
  return FIELD_KIND_BY_PATH[fieldPath] ?? 'STRING';
}

export function getNewCondition(): InvoiceTransformationCondition {
  return {
    operator: InvoiceTransformationConditionOperatorEnum.EQ,
    field: '',
    value: '',
  };
}

export function getNewConditionGroup(
  mode: InvoiceTransformationOutputModeEnum = InvoiceTransformationOutputModeEnum.MULTIPLE_INVOICES,
): InvoiceTransformationConditionGroup {
  return {
    target: getDefaultTarget(mode),
    conditions: [],
    groupBy: [],
  };
}

export type UiLineRule =
  InvoiceTransformationRequestUi['invoiceLineRules'][number];

export function getNewLineRule(
  mode: InvoiceTransformationOutputModeEnum = InvoiceTransformationOutputModeEnum.MULTIPLE_INVOICES,
): UiLineRule {
  return {
    name: '',
    priority: 0,
    conditionGroups: [getNewConditionGroup(mode)],
    billGroupOverrides: [],
  };
}

function overridesObjectToRows(
  overrides: InvoiceTransformationBillGroupOverride | undefined,
): BillGroupOverrideRow[] {
  if (!overrides) {
    return [];
  }
  return OVERRIDE_KEY_OPTIONS.flatMap(({ value: key }) => {
    const value = overrides[key];
    if (value === undefined || value === null) {
      return [];
    }
    return [{ key, value }];
  });
}

function overrideRowsToObject(
  rows: BillGroupOverrideRow[] | undefined,
): InvoiceTransformationBillGroupOverride | undefined {
  if (!rows || rows.length === 0) {
    return undefined;
  }
  const result: InvoiceTransformationBillGroupOverride = {};
  for (const { key, value } of rows) {
    if (key && value !== undefined) {
      result[key] = value;
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

export function toUiForm(
  billGroupId: string,
  response: InvoiceTransformationResponse | null | undefined,
): InvoiceTransformationRequestUi {
  // Only one output mode is allowed at a time, so we read the first
  // transformation from the response and default to MULTIPLE_INVOICES when none
  // exists yet.
  const transformation = response?.invoiceTransformations?.[0];
  const mode =
    transformation?.output?.mode ??
    InvoiceTransformationOutputModeEnum.MULTIPLE_INVOICES;
  const lineRules = transformation?.invoiceLineRules ?? [];
  return {
    billGroupId,
    mode,
    invoiceLineRules: lineRules.map((rule) => ({
      name: rule.name,
      priority: rule.priority,
      conditionGroups: rule.conditionGroups.map((group) => ({
        target: group.target,
        conditions: group.conditions.map((condition) => ({
          operator: condition.operator,
          field: condition.field,
          value: condition.value ?? '',
          values: condition.values ?? [],
        })),
        groupBy: group.groupBy ?? [],
      })),
      billGroupOverrides: overridesObjectToRows(rule.billGroupOverrides),
    })),
  };
}

export function toApiPayload(
  uiForm: InvoiceTransformationRequestUi,
): InvoiceTransformationRequest {
  const includeOverrides = supportsBillGroupOverrides(uiForm.mode);
  const invoiceLineRules = uiForm.invoiceLineRules.map(
    (rule, index): InvoiceLineRule => ({
      name: rule.name,
      priority: index,
      conditionGroups: rule.conditionGroups.map((group) => {
        const conditions = group.conditions.map((condition) => {
          const isMulti = isMultiValueOperator(condition.operator);
          return {
            operator: condition.operator,
            field: condition.field,
            value: isMulti ? undefined : condition.value,
            values: isMulti ? condition.values : undefined,
          };
        });
        return {
          target: group.target,
          conditions,
          groupBy:
            group.groupBy && group.groupBy.length > 0
              ? group.groupBy
              : undefined,
        };
      }),
      // Bill group overrides only apply when splitting into multiple invoices.
      billGroupOverrides: includeOverrides
        ? overrideRowsToObject(rule.billGroupOverrides)
        : undefined,
    }),
  );

  return {
    billGroupId: uiForm.billGroupId,
    invoiceTransformations: [
      {
        output: { mode: uiForm.mode },
        invoiceLineRules,
      },
    ],
  };
}

const TARGET_TITLE_BY_VALUE: Record<InvoiceTransformationTargetEnum, string> =
  TARGET_OPTIONS.reduce(
    (acc, { value, title }) => {
      acc[value] = title;
      return acc;
    },
    {} as Record<InvoiceTransformationTargetEnum, string>,
  );

function fieldDescription(
  fieldPath: string,
  availableFields: InvoiceTransformationAvailableField[],
): string {
  return (
    availableFields.find((field) => field.fieldPath === fieldPath)
      ?.description ?? fieldPath
  );
}

/**
 * Builds a short, human-readable one-line summary of a rule for the collapsed
 * card and the read-only view, e.g.:
 *   "Usage Event · 2 conditions · grouped by Team, Region"
 *   "Invoice Item · all lines · no grouping"
 */
export function summarizeRule(
  rule: UiLineRule,
  availableFields: InvoiceTransformationAvailableField[],
): string {
  const groups = rule.conditionGroups ?? [];
  const targetLabel =
    groups.length > 0 ? TARGET_TITLE_BY_VALUE[groups[0].target] : '';

  const conditionCount = groups.reduce(
    (total, group) => total + (group.conditions?.length ?? 0),
    0,
  );
  const conditionLabel =
    conditionCount === 0
      ? 'all lines'
      : `${formatInteger(conditionCount)} ${pluralize('condition', conditionCount)}`;

  const groupByFields = groups.flatMap((group) => group.groupBy ?? []);
  const uniqueGroupByFields = [...new Set(groupByFields)];
  const groupingLabel =
    uniqueGroupByFields.length === 0
      ? 'no grouping'
      : `grouped by ${uniqueGroupByFields
          .map((fieldPath) => fieldDescription(fieldPath, availableFields))
          .join(', ')}`;

  return [targetLabel, conditionLabel, groupingLabel]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Reads the message of an error attached to an array itself (e.g. "too many
 * rules"), as opposed to one of its entries. React Hook Form files it under
 * `root` when the array's entries are registered fields, and directly on the
 * array otherwise.
 */
export function getArrayErrorMessage(
  error: { message?: string; root?: { message?: string } } | undefined,
): string | undefined {
  return error?.root?.message ?? error?.message;
}
